// Custom hook for team transcripts list data fetching and management

import { useState, useEffect, useCallback } from 'react';
import type { 
  TeamTranscript, 
  TeamTranscriptListParams, 
  TeamTranscriptListResponse,
  TeamTranscriptListState 
} from '../types/teamTranscripts';

export const useTeamTranscripts = (teamId: string, refreshTrigger: number = 0) => {
  const [state, setState] = useState<TeamTranscriptListState>({
    transcripts: [],
    pagination: null,
    team: null,
    loading: true,
    error: null,
    search: '',
    selectedMemberId: null
  });

  // Fetch team transcripts
  const fetchTranscripts = useCallback(async (params: TeamTranscriptListParams = {}) => {
    if (!teamId) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.memberId) queryParams.append('memberId', params.memberId);

      const response = await fetch(`/api/teams/${teamId}/transcripts/list?${queryParams}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: TeamTranscriptListResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch team transcripts');
      }

      setState(prev => ({
        ...prev,
        transcripts: result.transcripts || [],
        pagination: result.pagination || null,
        team: result.team || null,
        loading: false,
        error: null
      }));

    } catch (error) {
      console.error('Error fetching team transcripts:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch team transcripts'
      }));
    }
  }, [teamId]);

  // Load more transcripts (pagination)
  const loadMore = useCallback(async () => {
    if (!state.pagination?.hasMore || state.loading) return;

    const currentOffset = state.pagination.offset + state.pagination.limit;
    
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('limit', state.pagination.limit.toString());
      queryParams.append('offset', currentOffset.toString());
      if (state.search) queryParams.append('search', state.search);
      if (state.selectedMemberId) queryParams.append('memberId', state.selectedMemberId);

      const response = await fetch(`/api/teams/${teamId}/transcripts/list?${queryParams}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: TeamTranscriptListResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to load more transcripts');
      }

      setState(prev => ({
        ...prev,
        transcripts: [...prev.transcripts, ...(result.transcripts || [])],
        pagination: result.pagination || null,
      }));

    } catch (error) {
      console.error('Error loading more transcripts:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load more transcripts'
      }));
    }
  }, [teamId, state.pagination, state.loading, state.search, state.selectedMemberId]);

  // Search transcripts
  const searchTranscripts = useCallback((searchQuery: string) => {
    setState(prev => ({ ...prev, search: searchQuery }));
    fetchTranscripts({ 
      limit: 20, 
      offset: 0, 
      search: searchQuery,
      memberId: state.selectedMemberId || undefined
    });
  }, [fetchTranscripts, state.selectedMemberId]);

  // Filter by member
  const filterByMember = useCallback((memberId: string | null) => {
    setState(prev => ({ ...prev, selectedMemberId: memberId }));
    fetchTranscripts({ 
      limit: 20, 
      offset: 0, 
      search: state.search,
      memberId: memberId || undefined
    });
  }, [fetchTranscripts, state.search]);

  // Refresh transcripts
  const refresh = useCallback(() => {
    fetchTranscripts({ 
      limit: 20, 
      offset: 0, 
      search: state.search,
      memberId: state.selectedMemberId || undefined
    });
  }, [fetchTranscripts, state.search, state.selectedMemberId]);

  // Initial load and refresh trigger
  useEffect(() => {
    fetchTranscripts({ limit: 20, offset: 0 });
  }, [fetchTranscripts, refreshTrigger]);

  return {
    ...state,
    loadMore,
    searchTranscripts,
    filterByMember,
    refresh,
    hasMore: state.pagination?.hasMore || false
  };
};